/**
 * Subscription API Client
 *
 * API functions for subscription tier management.
 * Currently uses mock data until backend endpoints are ready.
 */

import { apiClient } from '../api-client'
import type {
  Tier,
  WorkspaceSubscription,
  FeatureUsage,
  UpgradeSubscriptionInput,
  UpgradeSubscriptionResponse,
  TierUpgradeOption,
} from '@/types/subscription'

// Mock data flag - backend is ready!
const USE_MOCK_DATA = false

// =====================================================
// MOCK DATA
// =====================================================

const MOCK_TIERS: Tier[] = [
  {
    id: 'tier-free',
    code: 'free',
    name: 'Free',
    display_name: { de: 'Kostenlos', en: 'Free' },
    description: { de: 'Für den Einstieg', en: 'Get started' },
    monthly_price: 0,
    annual_price: 0,
    currency: 'EUR',
    display_order: 1,
    color: '#6B7280',
    icon: null,
    marketing_features: ['10 Produkte', '100 Bilder/Monat', 'Standard Support'],
    is_public: true,
    is_active: true,
    is_custom: false,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'tier-starter',
    code: 'starter',
    name: 'Starter',
    display_name: { de: 'Starter', en: 'Starter' },
    description: { de: 'Für kleine Teams', en: 'For small teams' },
    monthly_price: 29,
    annual_price: 290,
    currency: 'EUR',
    display_order: 2,
    color: '#FF8C00',
    icon: null,
    marketing_features: ['100 Produkte', '1.000 Bilder/Monat', 'API Zugriff', 'Priority Support'],
    is_public: true,
    is_active: true,
    is_custom: false,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
  {
    id: 'tier-pro',
    code: 'pro',
    name: 'Pro',
    display_name: { de: 'Pro', en: 'Pro' },
    description: { de: 'Für wachsende Unternehmen', en: 'For growing businesses' },
    monthly_price: 99,
    annual_price: 990,
    currency: 'EUR',
    display_order: 3,
    color: '#FF6B00',
    icon: null,
    marketing_features: [
      'Unbegrenzte Produkte',
      '10.000 Bilder/Monat',
      'API Zugriff',
      'Batch Generation',
      '4K Bilder',
      'Premium Support',
    ],
    is_public: true,
    is_active: true,
    is_custom: false,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
  },
]

const MOCK_SUBSCRIPTION: WorkspaceSubscription = {
  tier: MOCK_TIERS[0],
  features: [
    {
      feature_code: 'pim_products',
      feature_name: 'Products',
      category: 'PIM',
      limit_type: 'numeric',
      limit: 10,
      current: 7,
      unit: 'products',
      is_premium: false,
      is_beta: false,
    },
    {
      feature_code: 'ai_images_monthly',
      feature_name: 'AI Images/Month',
      category: 'AI',
      limit_type: 'numeric',
      limit: 100,
      current: 45,
      unit: 'images',
      is_premium: false,
      is_beta: false,
    },
  ],
  is_grandfathered: false,
  custom_price: null,
  is_trial: false,
  trial_ends_at: null,
  next_payment_at: null,
  payment_method: null,
  can_upgrade_to: [],
  assignment_history: [],
}

// =====================================================
// API FUNCTIONS
// =====================================================

/**
 * Fetch all public tiers (pricing page)
 */
export async function getAvailablePlans(): Promise<Tier[]> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 300))
    return MOCK_TIERS.filter((t) => t.is_public && t.is_active)
  }

  const response = await apiClient.get<{ tiers: Tier[] }>('/api/v2/subscription/plans')
  return response.data.tiers
}

/**
 * Fetch current workspace subscription
 */
export async function getCurrentSubscription(): Promise<WorkspaceSubscription> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 300))
    return MOCK_SUBSCRIPTION
  }

  const response = await apiClient.get<WorkspaceSubscription>('/api/v2/subscription/current')
  return response.data
}

/**
 * Fetch feature usage stats for current workspace
 */
export async function getFeatureUsage(): Promise<FeatureUsage[]> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 200))
    return MOCK_SUBSCRIPTION.features.map((f) => ({
      feature_code: f.feature_code,
      feature_name: f.feature_name,
      current: f.current || 0,
      limit: typeof f.limit === 'number' ? f.limit : 0,
      percentage: f.current && typeof f.limit === 'number' ? (f.current / f.limit) * 100 : 0,
      unit: f.unit,
      status: 'safe' as const,
    }))
  }

  const response = await apiClient.get<{ usage: FeatureUsage[] }>('/api/v2/subscription/usage')
  return response.data.usage
}

/**
 * Get available upgrade options for current tier
 */
export async function getUpgradeOptions(): Promise<TierUpgradeOption[]> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 200))
    const currentTierIdx = MOCK_TIERS.findIndex((t) => t.id === MOCK_SUBSCRIPTION.tier.id)
    return MOCK_TIERS.slice(currentTierIdx + 1).map((tier) => ({
      tier,
      price_difference: tier.monthly_price - MOCK_SUBSCRIPTION.tier.monthly_price,
      feature_improvements: [],
    }))
  }

  const response = await apiClient.get<{ options: TierUpgradeOption[] }>(
    '/api/v2/subscription/upgrade-options'
  )
  return response.data.options
}

/**
 * Upgrade subscription to a new tier
 */
export async function upgradeSubscription(
  input: UpgradeSubscriptionInput
): Promise<UpgradeSubscriptionResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 500))
    const newTier = MOCK_TIERS.find((t) => t.id === input.tier_id)
    if (!newTier) throw new Error('Tier not found')
    return {
      success: true,
      assignment: { ...MOCK_SUBSCRIPTION, tier: newTier },
      payment_status: 'completed',
      next_steps: ['Subscription aktiv', 'Neue Features verfügbar'],
    }
  }

  const response = await apiClient.post<UpgradeSubscriptionResponse>(
    '/api/v2/subscription/upgrade',
    input
  )
  return response.data
}

/**
 * Cancel a scheduled downgrade
 */
export async function cancelScheduledDowngrade(): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.post<{ success: boolean; message?: string }>(
    '/api/v2/subscription/cancel-downgrade'
  )
  return response.data
}

/**
 * Cancel subscription at period end
 */
export async function cancelSubscription(): Promise<{ status: string; effective_date: string }> {
  const response = await apiClient.post<{ status: string; effective_date: string }>(
    '/api/v2/subscription/cancel'
  )
  return response.data
}

/**
 * Reactivate a canceled subscription
 */
export async function reactivateSubscription(): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    '/api/v2/subscription/reactivate'
  )
  return response.data
}

/**
 * Downgrade/cancel subscription
 */
export async function downgradeSubscription(reason?: string): Promise<{ success: boolean }> {
  if (USE_MOCK_DATA) {
    await new Promise((r) => setTimeout(r, 500))
    return { success: true }
  }

  const response = await apiClient.post<{ success: boolean }>(
    '/api/v2/subscription/downgrade',
    { reason }
  )
  return response.data
}
